// The StepperInput's value arithmetic, kept pure: what the count control
// steps to, and what a paste is allowed to drop into the field.

export interface StepperMode {
  /** Accept decimals and a leading minus (the generator's field bounds);
   *  integer fields keep the digits-only rule. */
  decimal: boolean;
  /** Floor for the count control; typed values are never rewritten. */
  min: number;
}

// An empty field steps from zero, not from the placeholder default — the
// first increment gives 1, however large the default shown behind it.
export function stepped(
  value: string,
  delta: number,
  { decimal, min }: StepperMode,
): string {
  const current = decimal
    ? Number.parseFloat(value)
    : Number.parseInt(value, 10);
  const base = Number.isNaN(current) ? 0 : current;
  const next = Math.max(min, base + delta);
  // Float noise (0.1 + 1 = 1.1000000000000001) never reaches the field.
  return decimal ? String(Number(next.toFixed(10))) : String(next);
}

const INTEGER_TEXT = /^\d+$/;
const DECIMAL_TEXT = /^-?(\d+\.?\d*|\.\d+)$/;

/** What a pasted text should become: null keeps the browser's own paste
 *  (the text is already a clean number), a string replaces it with what
 *  survives the filter, "" means nothing usable was pasted. */
export function pasteReplacement(
  text: string,
  decimal: boolean,
): string | null {
  const trimmed = text.trim();
  if ((decimal ? DECIMAL_TEXT : INTEGER_TEXT).test(trimmed)) return null;
  if (!decimal) return trimmed.replace(/\D/g, "");
  const sign = trimmed.startsWith("-") ? "-" : "";
  const [whole, ...rest] = trimmed.replace(/[^\d.]/g, "").split(".");
  const fraction = rest.join("");
  const digits = fraction ? `${whole}.${fraction}` : whole;
  return digits ? `${sign}${digits}` : "";
}

/** Keys type="number" accepts that the field should not: exponent and plus
 *  always, minus and the decimal point on integer fields. */
export function isBlockedKey(key: string, decimal: boolean): boolean {
  if (["e", "E", "+"].includes(key)) return true;
  return !decimal && (key === "-" || key === ".");
}

/** The native constraints the input carries for the mode: a finite floor
 *  as `min`, and `step="any"` so decimals pass the browser's validation. */
export function numberInputAttrs({ decimal, min }: StepperMode): {
  min: number | undefined;
  step: "any" | undefined;
} {
  return {
    min: Number.isFinite(min) ? min : undefined,
    step: decimal ? "any" : undefined,
  };
}
